import { DEF } from '../constants/defaults';
import { F, FF } from '../utils/format';
import { Card } from '../components/Card';
import { Heading } from '../components/Heading';
import { ProgressBar } from '../components/ProgressBar';
import { Stack } from '../components/Stack';
import { Check, Plus } from '../components/icons';
import { useApp } from '../hooks/useApp';

export default function Milestones() {
  const { s, c, proj, pF, p0, up, upM, setS, tt, fiAge, fiReached, coastAge, budgetMonthly, blendedReturn, realReturn, amor, scen, mcData, mcRunning, runMonteCarlo, showToast, setConfirm, fileRef } = useApp();
  const maxA = s.maxAge || 40;
  const allMs = (s.customMs || DEF.customMs).filter(m => m.a <= maxA);
  const editMs = (idx, field, val) => {
    const updated = [...(s.customMs || DEF.customMs)];
    updated[idx] = { ...updated[idx], [field]: val };
    up("customMs", updated);
  };
  const addMs = () => {
    const ms = s.customMs || DEF.customMs;
    up("customMs", [...ms, { a: s.age + 5, nw: 500000, l: "New milestone" }]);
  };
  const removeMs = (idx) => {
    up("customMs", (s.customMs || DEF.customMs).filter((_, i) => i !== idx));
  };

  return (
    <Stack>
      <Card c={c} sx={{ padding: "28px 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: c.muted, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 8 }}>
              Financial Independence
            </div>
            <div style={{ fontSize: 44, fontWeight: 800, letterSpacing: "-0.04em", color: c.fg }}>
              {maxA - s.age} Year Plan
            </div>
          </div>
          <button onClick={addMs} style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "8px 16px", borderRadius: 10, border: `1px solid ${c.border}`,
            background: "transparent", color: c.blue, fontSize: 13, fontWeight: 600, cursor: "pointer",
          }}>
            <Plus size={14} /> Add Milestone
          </button>
        </div>
      </Card>
      <div style={{ position: "relative", paddingLeft: 40 }}>
        <div style={{
          position: "absolute", left: 15, top: 0, bottom: 0, width: 2,
          background: `linear-gradient(180deg, ${c.blue}, ${c.green})`, borderRadius: 1,
        }} />
        {allMs.map((m, idx) => {
          const pd = proj.find((d) => d.a === m.a);
          const nw = pd?.nw || 0;
          const done = s.ms?.[m.a];
          const ok = nw >= m.nw;
          return (
            <div key={idx} style={{ position: "relative", paddingBottom: 20, animation: `fadeSlideUp 0.5s cubic-bezier(0.25,0.1,0.25,1) ${idx * 0.1}s both` }}>
              <div style={{
                position: "absolute", left: -36, top: 4,
                width: 24, height: 24, borderRadius: 12,
                background: done ? c.green : ok ? c.blue : c.card,
                border: `2px solid ${done ? c.green : ok ? c.blue : c.border}`,
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.3s cubic-bezier(0.25,0.1,0.25,1)",
                boxShadow: done ? `0 0 12px ${c.green}40` : ok ? `0 0 12px ${c.blue}30` : "none",
              }}>
                {done && <Check size={13} color="#fff" />}
              </div>
              <Card c={c}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span style={{ fontSize: 20, fontWeight: 700, color: c.fg }}>Age {m.a}</span>
                      <button onClick={() => removeMs(idx)} style={{
                        padding: "2px 6px", borderRadius: 6, border: `1px solid ${c.border}`,
                        background: "transparent", color: c.muted, fontSize: 10, cursor: "pointer",
                      }}>✕</button>
                    </div>
                    <input value={m.l} onChange={(e) => editMs(idx, "l", e.target.value)}
                      style={{
                        background: "transparent", border: "none", color: c.muted,
                        fontSize: 13, width: "100%", outline: "none", padding: 0,
                      }} />
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 11, color: c.muted, fontWeight: 600, textTransform: "uppercase" }}>Projected</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: ok ? c.green : c.orange, fontVariantNumeric: "tabular-nums", marginTop: 2 }}>
                      {F(nw)}
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
                  <span style={{ fontSize: 12, color: c.muted, whiteSpace: "nowrap" }}>Target:</span>
                  <input type="number" value={m.nw}
                    onChange={(e) => editMs(idx, "nw", Number(e.target.value) || 0)}
                    style={{
                      width: 110, padding: "4px 8px", borderRadius: 8,
                      border: `1px solid ${c.border}`, background: c.inputBg,
                      color: c.fg, fontSize: 13, fontWeight: 600, outline: "none",
                      fontVariantNumeric: "tabular-nums",
                    }} />
                  <span style={{ fontSize: 12, color: c.muted, whiteSpace: "nowrap" }}>Age:</span>
                  <input type="number" value={m.a}
                    onChange={(e) => editMs(idx, "a", Number(e.target.value) || s.age)}
                    style={{
                      width: 55, padding: "4px 8px", borderRadius: 8,
                      border: `1px solid ${c.border}`, background: c.inputBg,
                      color: c.fg, fontSize: 13, fontWeight: 600, outline: "none",
                    }} />
                </div>
                <ProgressBar v={nw} mx={m.nw} l={`Target: ${F(m.nw)}`} color={ok ? c.green : c.blue} c={c} />
                <button onClick={() => up("ms", { ...s.ms, [m.a]: !done })}
                  style={{
                    padding: "8px 20px", borderRadius: 20, fontSize: 13,
                    fontWeight: 600, cursor: "pointer",
                    border: done ? "none" : `1px solid ${c.border}`,
                    background: done ? c.green : "transparent",
                    color: done ? "#fff" : c.fg,
                    transition: "all 0.25s cubic-bezier(0.25,0.1,0.25,1)",
                  }}>
                  {done ? "Achieved ✓" : "Mark Achieved"}
                </button>
              </Card>
            </div>
          );
        })}
      </div>
    </Stack>
  );
}
